import React, {useState} from 'react';
import {Article, ArticleData} from "types/Article";
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import FieldHeader from "components/Form/FieldHeader";
import FormControls from "components/FormControls";
import Toggle from "components/Form/Toggle";
import {Tab} from '@headlessui/react';
import TabButton from "components/TabButton";
import {DateTime} from "luxon";
import Select from "components/Form/Select";
import ngage from "services/ngage";
import {Tag} from "types/Tag";
import {Option} from "components/Form/Select/Select";
import LinkEditor from "../../../components/Form/LinkEditor";
import {LinkUrlFieldSet, SiteAttachmentFieldSet} from "../../../components/Form/Form/Fieldset";
import IconName from "../../../types/IconName";
import {LinkAPIData} from "../../../types/Link";
import Button from "../../../components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Well from "../../../components/Well";
import {MediaField, SelectField, TextAreaField, TextField} from "../../../components/Form/Form";
import {linkUrlRegex, toTitleCase} from "../../../services/string";
import {DateField} from "../../../components/Form/Form/Field";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {urlTest} from "../../../services/validation-schemas";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type Props = {
  article?: Article | null
  onSubmit: (values: ArticleData, helpers: FormikHelpers<ArticleData>) => any
}

// Helpers ----

const tagOption = (tag: Tag): Option => ({
  id: tag.id,
  name: tag.name,
  icon: IconName.TAG
});

// Get all icon names as options for a select input
const iconOptions: Array<Option> = Object.values(IconName).map(icon => ({
  id: icon, name: toTitleCase(icon), icon: icon
}))

// Component ----

function ArticleForm({article, onSubmit}: Props) {
  const {termFor} = useOperator();
  const [publish, setPublish] = useState<boolean>(!!article?.published_at);
  const [selectedTags, setSelectedTags] = useState<Array<Option>>(article?.tags.map(tagOption) ?? []);

  // Tags ----
  const tagSource = async (query: string) => {
    return (await ngage.tags.filtered({search: query || null}))?.data.map(tagOption) || []
  }

  async function addTag(name: string) {
    try {
      const tag = await ngage.tags.store({name})

      return tag ? tagOption(tag) : null
    } catch (error) {
      return null
    }
  }

  function handleSelectTags(tags: Array<Option>, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('tag_ids', tags.map(tag => tag.id))
    setSelectedTags(tags)
  }

  function selectContentMode(index: number, {setFieldValue}: FormikHelpers<any>) {
    const isExternal = !!index
    void setFieldValue('is_external', isExternal)

    if (isExternal) {
      void setFieldValue('body', undefined)
      void setFieldValue('links', [])
    } else {
      void setFieldValue('source_name', undefined)
      void setFieldValue('source_url', undefined)
    }
  }

  function handlePublishToggle(enabled: boolean, {setFieldValue}: FormikHelpers<any>) {
    setPublish(enabled)

    if (enabled) {
      void setFieldValue('published_at', DateTime.utc().startOf('minute').toISO())
    } else {
      void setFieldValue('published_at', null)
      void setFieldValue('is_featured', false)
    }
  }

  function handleFeatureToggle(enabled: boolean, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('is_featured', enabled)
  }

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("This field is required").max(250, "Maximum of 255 characters"),
    excerpt: Yup.string().max(250, "Maximum character limit exceeded"),
    is_external: Yup.boolean(),
    body: Yup.string().when('is_external', ([isExternal]: any, schema: any) => {
      if (!isExternal) return schema.required('This field is required').max(50000, "Maximum character limit exceeded");
    }),
    source_name: Yup.string().when('is_external', ([isExternal]: any, schema: any) => {
      if (isExternal) return schema.required('This field is required');
    }),
    source_url: Yup.string().when('is_external', ([isExternal]: any, schema: any) => {
      if (isExternal) return schema
        .required('This field is required')
        .max(250, 'Maximum 250 characters')
        .test('valid-url', urlTest)
    }),
    links: Yup.array().of(Yup.object().shape({
      text: Yup.string().required('This field is required'),
      url: Yup.string().required('This field is required').matches(linkUrlRegex, 'This field is required'),
      icon: Yup.string().required('This field is required'),
    })),
    published_at: publish ? Yup.date().required('This field is required') : Yup.date().nullable(),
  })

  const initialValues: ArticleData = {
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    is_external: article?.is_external || false,
    body: (!article?.is_external) ? article?.body : undefined,
    source_name: article?.is_external ? article.source.name : undefined,
    source_url: article?.is_external ? article.source.url : undefined,
    media_ids: article?.media.map(media => media.id) || [],
    tag_ids: article?.tags.map(tag => tag.id) || [],
    links: article?.links.map(({id, text, icon, url, offline_url}) => ({id, text, icon, url, offline_url})) || [],
    published_at: article?.published_at?.toUTC().toISO() || undefined,
    is_featured: article?.is_featured || false,
    site_ids: article?.sites.map(site => site.id)
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Article Content
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        The main content of the article, or a description of where it can be found
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                      {/* Title */}
                      <div className="sm:col-span-4 xl:col-span-3">
                        <Field
                          label="Title *"
                          hint="250 chars max"
                          type="text"
                          name="title"
                          maxLength={250}
                          autoFocus
                        />
                      </div>

                      {/* Tags */}
                      <div className="sm:col-span-6">
                        <Select
                          label="Tags"
                          hint="Type to search"
                          source={tagSource}
                          selected={selectedTags}
                          onNew={addTag}
                          exactMatch
                          onChange={selected => handleSelectTags(selected, formik)}
                          multiple
                        />
                      </div>

                      {/* Excerpt */}
                      <div className="sm:col-span-6">
                        <TextAreaField
                          label="Excerpt"
                          hint="Optional. Max chars 250"
                          id="excerpt"
                          name="excerpt"
                          maxChars={250}
                          rows={3}
                        />
                      </div>

                      {/* Media */}
                      <div className="sm:col-span-6">
                        <MediaField
                          name="media_ids"
                          label="Media"
                          files={article?.media || []}
                          type={[MediaField.types.AUDIO, MediaField.types.IMAGE, MediaField.types.VIDEO]}
                        />
                      </div>

                      {/* Content */}
                      <div className="sm:col-span-6">
                        <FieldHeader label="Source"/>
                        <Tab.Group
                          selectedIndex={Number(formik.values.is_external)}
                          onChange={index => selectContentMode(index, formik)}
                        >
                          <Tab.List className="mt-1">
                            <TabButton position={TabButton.positions.FIRST} disabled={!!article}>Internal</TabButton>
                            <TabButton position={TabButton.positions.LAST} disabled={!!article}>External</TabButton>
                          </Tab.List>
                          <Tab.Panels className="mt-1">
                            {/* Internal */}
                            <Tab.Panel>
                              <div className='mt-4'>
                                <TextAreaField
                                  name='body'
                                  id="body"
                                  label='Body *'
                                  hint="50,000 chars max"
                                  maxChars={50000}
                                  rows={10}
                                />
                              </div>

                              {/* Links */}
                              <div className="mt-4 sm:col-span-6">
                                <FieldHeader label="Links"/>
                                <FieldArray name={'links'}>
                                  {({push, remove, form}: FieldArrayRenderProps) => (
                                    <>
                                      <ul className="mt-1">
                                        {form.values.links.map((_link: LinkAPIData, index: number) => (
                                          <li key={index} className="mb-4">
                                            <Well>
                                              <div className="flex space-x-2">
                                                <div className="flex-grow sm:grid sm:grid-cols-6 sm:gap-x-2 sm:gap-y-4">
                                                  <div className="sm:col-span-6">
                                                    <LinkUrlFieldSet
                                                      label="Link To"
                                                      name={`links.${index}.url`}
                                                      offlineName={`links.${index}.offline_url`}
                                                    />
                                                  </div>

                                                  <div className="sm:col-span-4">
                                                    <TextField
                                                      name={`links.${index}.text`}
                                                      label="Text *"
                                                    />
                                                  </div>

                                                  <div className="sm:col-span-2">
                                                    <SelectField
                                                      name={`links.${index}.icon`}
                                                      label="Icon *"
                                                      options={iconOptions}
                                                    />
                                                  </div>
                                                </div>

                                                <div className="flex-grow-0">
                                                  <Button
                                                    size={Button.sizes.X_SMALL}
                                                    color={Button.colors.WHITE}
                                                    icon={TrashIcon}
                                                    onClick={() => remove(index)}
                                                  />
                                                </div>
                                              </div>
                                            </Well>
                                          </li>
                                        ))}
                                      </ul>

                                      <div className={form.values.links?.length ? 'mt-4' : ''}>
                                        <Button
                                          color={Button.colors.SECONDARY}
                                          icon={PlusIcon}
                                          size={Button.sizes.TINY}
                                          onClick={() => push({text: '', url: '', icon: 'link'})}
                                        >
                                          Add Link
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </FieldArray>
                              </div>
                            </Tab.Panel>

                            {/* External */}
                            <Tab.Panel>
                              <LinkEditor textName="source_name" urlName="source_url" schemes={["https"]}/>
                            </Tab.Panel>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {termFor('Sites')}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose which {termFor('sites')} to associate this with.
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <SiteAttachmentFieldSet name={"site_ids"} selected={article?.sites}/>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Publishing
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose how and where to publish this article
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2 lg:col-span-1 mt-1">
                        <Toggle
                          label="Publish"
                          checked={publish}
                          onChange={checked => handlePublishToggle(checked, formik)}
                        />
                      </div>

                      {publish &&
                        <>
                          <div className="sm:col-span-3 lg:col-span-2">
                            <DateField
                              type='datetime-local'
                              name='published_at'
                              id='published_at'
                              autoComplete='off'
                            />
                          </div>

                          <div className="sm:col-span-6">
                            <Toggle
                              label="Featured"
                              description="Mark this as a featured article to display it prominently to your audience"
                              multiline
                              checked={formik.values.is_featured}
                              onChange={checked => handleFeatureToggle(checked, formik)}
                            />
                          </div>
                        </>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default ArticleForm;